import { PushSubscriptionItem } from './offlineDataTypes';

export class PushService {
  private static VAPID_PUBLIC_KEY = 'BC_YAMOOPAY_PROD_VAPID_PUBLIC_KEY_STANDARDS_2026_EXCELLENCE';

  static isPushSupported(): boolean {
    return (
      typeof window !== 'undefined' &&
      'serviceWorker' in navigator &&
      'PushManager' in window &&
      'Notification' in window
    );
  }

  static getPermissionState(): NotificationPermission {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission;
    }
    return 'default';
  }

  static async requestContextualPermission(purpose: string): Promise<boolean> {
    if (!this.isPushSupported()) return false;

    console.log(`[PushService] Requesting permission for purpose: ${purpose}`);
    try {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    } catch (e) {
      console.warn('[PushService] Notification permission request error:', e);
      return false;
    }
  }

  static async subscribeToCategory(
    category: 'FX_ALERT' | 'TAX_UPDATE' | 'SECURITY' | 'SCENARIO_REMINDER'
  ): Promise<PushSubscriptionItem | null> {
    if (!this.isPushSupported()) return null;

    const granted = await this.requestContextualPermission(`Alerts for ${category}`);
    if (!granted) return null;

    const item: PushSubscriptionItem = {
      id: `sub-${Date.now()}`,
      endpoint: `https://fcm.googleapis.com/fcm/send/yamoopay-${Math.random().toString(36).substring(2, 9)}`,
      platform: 'CHROME_ANDROID',
      subscribedAt: new Date().toISOString(),
      categories: [category],
      authKeyPresent: true,
      p256dhPresent: true,
      isActive: true
    };

    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const saved = localStorage.getItem('yamoopay_push_subs');
        const list: PushSubscriptionItem[] = saved ? JSON.parse(saved) : [];
        list.push(item);
        localStorage.setItem('yamoopay_push_subs', JSON.stringify(list));
      }
    } catch (e) {
      // ignore
    }

    return item;
  }

  static setBadge(count: number): void {
    if (typeof navigator !== 'undefined') {
      if ('setAppBadge' in navigator) {
        (navigator as any).setAppBadge(count).catch(() => {});
      } else if ('setExperimentalAppBadge' in navigator) {
        (navigator as any).setExperimentalAppBadge(count).catch(() => {});
      }
    }
  }

  static clearBadge(): void {
    if (typeof navigator !== 'undefined') {
      if ('clearAppBadge' in navigator) {
        (navigator as any).clearAppBadge().catch(() => {});
      } else if ('clearExperimentalAppBadge' in navigator) {
        (navigator as any).clearExperimentalAppBadge().catch(() => {});
      }
    }
  }

  /**
   * Sanitizes notification text to strictly protect privacy on device lock screen.
   * Never displays exact bank balances, raw salaries, or mortgage amounts.
   */
  static formatPrivacySafeMessage(category: string, subject: string): { title: string; body: string } {
    switch (category) {
      case 'FX_ALERT':
        return {
          title: 'Currency Alert Reached',
          body: `Target rate for ${subject} was met. Tap to view mid-market analysis.`
        };
      case 'TAX_UPDATE':
        return {
          title: 'Tax Regulations Updated',
          body: `New official 2026 ruleset published for ${subject}.`
        };
      case 'SECURITY':
        return {
          title: 'YamooPay Security Notice',
          body: 'A new device accessed your workspace. Tap to review session.'
        };
      default:
        return {
          title: 'YamooPay Calculation Update',
          body: 'Your saved scenario has an updated projection.'
        };
    }
  }
}
